import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { UpdateDonationAmountModal } from '../UpdateDonationAmountModal/UpdateDonationAmountModal'

import { DonationMatchRequestStatus } from '@percent/admin-dashboard/api/actions/donationMatchRequests/donationMatchRequests.types'
import { ErrorView } from '@percent/admin-dashboard/common/components'
import { ExternalLink } from '@percent/admin-dashboard/common/components/externalLink/ExternalLink'
import { useQuery } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import { Badge, ButtonText, CardContent, CardDashboard, Flex, Loader } from '@percent/lemonade'
import { CurrencyCode, formatMoney } from '@percent/utility'
import styles from './DonationMatchRequestDetails.module.scss'

export type DonationMatchRequestDetailsProps = {
  amount: {
    amount: number
    currency: CurrencyCode
  }
  status: DonationMatchRequestStatus
  externalMatchingUrl?: string
  donationMadeBy: string | null
  donationDate?: string | null
  hireDate?: string | null
  isDonationDateEligibilityEnabled?: boolean
  id: string
  accountId: string
  refresh: VoidFunction
}

export function DonationMatchRequestDetails({
  amount,
  externalMatchingUrl,
  donationMadeBy,
  donationDate,
  hireDate,
  isDonationDateEligibilityEnabled = false,
  status,
  id,
  accountId,
  refresh
}: DonationMatchRequestDetailsProps) {
  const { t } = useTranslation()
  const { iamService } = useServices()
  const [{ data: getAccountDetails, isLoading, errorMessage }] = useQuery(iamService.getAccountDetail, {
    accountId
  })
  const [updateDonationAmountModalOpen, setUpdateDonationAmountModalOpen] = useState(false)
  const handleModal = () => {
    setUpdateDonationAmountModalOpen(prevState => !prevState)
  }

  const handleLinkClick = () => {
    const url = `/donation-match-requests?query=${accountId}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  if (isLoading) {
    return <Loader />
  }

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <CardDashboard title="Request details" className={styles.cardContentFixedLabels}>
        <CardContent title="Donation amount">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            {formatMoney(amount)}
            {(status === 'processing' || status === 'validating') && (
              <ButtonText onClick={() => setUpdateDonationAmountModalOpen(true)}>
                {t('button.donationMatchRequest.editDonationAmount')}
              </ButtonText>
            )}
          </div>
        </CardContent>
        {externalMatchingUrl && (
          <CardContent title={t('typography.matchToCampaign')}>
            <ExternalLink url={externalMatchingUrl} />
          </CardContent>
        )}
        {isDonationDateEligibilityEnabled && donationDate && (
          <CardContent title={t('typography.donationDate')}>
            {dayJS(donationDate).utc().format('D MMM YYYY')}
          </CardContent>
        )}
        {isDonationDateEligibilityEnabled && hireDate && (
          <CardContent title={t('typography.employeeStartDate')}>
            {dayJS(hireDate).utc().format('D MMM YYYY')}
          </CardContent>
        )}
        <CardContent title={t('typography.donorName')}>{getAccountDetails?.fullName}</CardContent>
        <CardContent title={t('typography.donorPreferredName')}>{getAccountDetails?.preferredName}</CardContent>
        <CardContent title={t('typography.donationMadeBy')}>
          <Flex align="center" gap="0.5rem">
            {donationMadeBy ?? getAccountDetails?.fullName}
            {donationMadeBy && <Badge variant="informative">{t('typography.spouse')}</Badge>}
          </Flex>
        </CardContent>
        <CardContent title={t('typography.donorEmail')}>{getAccountDetails?.email}</CardContent>
        <CardContent title={t('typography.donorAccountId')}>{accountId}</CardContent>
        <Flex mt="1rem" justify="flex-end">
          <ButtonText onClick={handleLinkClick}>{t('button.donationMatchRequest.moreMatchRequests')}</ButtonText>
        </Flex>
      </CardDashboard>
      {updateDonationAmountModalOpen && (
        <UpdateDonationAmountModal
          open={updateDonationAmountModalOpen}
          onClose={handleModal}
          donationMatchRequestId={id}
          amount={amount}
          refresh={refresh}
        />
      )}
    </>
  )
}
